'use client'

import _ from 'lodash'
import {
  CircleCheck,
  Link,
  Image,
  Download,
  PlayCircle
} from 'lucide-react'

import { Document } from '@/features/library/types/document'
import { useFinishResource } from '@/shared/hooks/useFinishResource'
import { cn } from '@/shared/lib/utils'
import { CardRewards } from '@/shared/ui/Card'

// external: abre um link (arquivo/link/vídeo/imagem). Caso contrário, é apenas
// um botão de conclusão (texto/incorporado), cujo conteúdo já é exibido no modal.
const kindConfig = {
  link: {
    getHref: (props: Document) => props.url || '',
    label: 'Acesse o link',
    icon: Link,
    download: false,
    external: true
  },
  archive: {
    getHref: (props: Document) => _.get(props.file, 'file.url', ''),
    label: 'Acesse o arquivo',
    icon: Download,
    download: true,
    external: true
  },
  image: {
    getHref: (props: Document) => _.get(props.image, 'file.url', ''),
    label: 'Acesse a imagem',
    icon: Image,
    download: false,
    external: true
  },
  video_url: {
    getHref: (props: Document) => props.url || '',
    label: 'Assista ao vídeo',
    icon: PlayCircle,
    download: false,
    external: true
  },
  text: {
    getHref: () => '',
    label: 'Marcar como lido',
    icon: CircleCheck,
    download: false,
    external: false
  },
  embedded: {
    getHref: () => '',
    label: 'Marcar como concluído',
    icon: CircleCheck,
    download: false,
    external: false
  }
} as const

function renderReward({
  hasRewards,
  hasFinished
}: {
  hasRewards: boolean
  hasFinished: boolean
}) {
  if (!hasRewards) return null
  return hasFinished ? (
    <span className="text-sm font-semibold flex items-center gap-1 text-gray-200">
      <CircleCheck className="size-4 " />
      <span className="block leading-tight ">Recompensa Já Coletada</span>
    </span>
  ) : (
    <span className="text-gray-200 text-sm font-normal flex items-center gap-1">
      <span className="block leading-tight">Recompensa Disponível</span>
    </span>
  )
}

export const DocumentModalButton = ({
  archive_kind,
  _progress,
  ...props
}: Document) => {
  const { finishResource } = useFinishResource()
  const kind = kindConfig[archive_kind as keyof typeof kindConfig]

  if (!kind) return null

  const href = kind.getHref(props)

  // Tipos externos (link/arquivo/imagem/vídeo) exigem um destino; sem ele não
  // há o que abrir. Tipos de conclusão (texto/incorporado) dispensam href.
  if (kind.external && !href) return null

  const Icon = kind.icon

  const handleFinishResource = () => {
    finishResource({
      type: 'Document',
      id: props.id,
      action: 'download'
    })
  }

  const className = cn(
    'bg-gradient-to-b from-[#28BD50] to-[#2FA86B] inset-shadow-[0_3px_0_0_rgba(179,255,56,0.39),_0_-3px_0px_#408144]',
    'relative top-0 left-0 w-full h-full flex items-center justify-start gap-3 text-white text-left',
    'pb-3 pt-3 px-3 rounded-md transition-all duration-100',
    {
      'bg-gradient-to-b from-[#3F80CA] to-[#3557AD] inset-shadow-[0_3px_0_0_rgba(179,255,56,0.39),_0_-3px_0px_#13224E]':
        _progress.finished,
      'bg-gradient-to-b from-[#28BD50] to-[#2FA86B] inset-shadow-[0_3px_0_0_rgba(179,255,56,0.39),_0_-3px_0px_#408144]':
        !_progress.finished
    }
  )

  const content = (
    <>
      <Icon className="size-9" />
      <div className="flex flex-col">
        <span className="text-md font-semibold block">{kind.label}</span>
        {renderReward({
          hasRewards: !!props.point_reward || !!props.coin_reward,
          hasFinished: _progress.finished
        })}
      </div>
      <CardRewards
        coinReward={props.coin_reward}
        pointReward={props.point_reward}
        completed={_progress.finished}
        claimed={_progress.claimed}
        gradient
        className="ms-auto border-0"
      />
    </>
  )

  return (
    <div className="d-flex gap-6">
      {kind.external ? (
        <a
          href={href}
          target="_blank"
          rel="noreferrer"
          download={kind.download || undefined}
          className={className}
          onClick={() => handleFinishResource()}
        >
          {content}
        </a>
      ) : (
        <button
          type="button"
          className={className}
          onClick={() => handleFinishResource()}
        >
          {content}
        </button>
      )}
    </div>
  )
}
